import { createPortal } from 'react-dom';
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react';
import { useTranslation } from 'react-i18next';
import { ChangeEvent, useRef, useState, KeyboardEvent, Fragment } from 'react';
import { IconNames } from '@sendible/design-system/src';
import { Container, HiddenInput, List, Spacer } from './index.styles';
import { Option } from './components';
import { composeMediaInteractions } from '../../composeMediaInteractions';
import { useComposeBoxContext } from '../../composeBoxContext';

interface OptionList {
  icon: IconNames;
  id: string;
  label: string;
  extraLabel?: string;
  onClick: () => void;
  onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
  useStroke?: boolean;
}

export const MediaAttacherDropdown = () => {
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    validateAndUploadFiles,
    isMediaAttacherOpen,
    activeComposeBoxTab,
    setIsCanvaModalOpen,
    setIsFlickrModalOpen,
    setIsGiphyModalOpen,
    setIsPexelsModalOpen,
    setIsRecentlyUsedModalOpen,
    setIsURLModalOpen,
    setIsYoutubeModalOpen,
  } = useComposeBoxContext();
  const { t } = useTranslation('common_terms');

  const recentlyUsed = 'Recently used';
  const fromUrl = 'From URL';
  const canva = 'Canva';
  const youtubeLink = 'Youtube link';
  const flickr = 'Flickr';
  const pexels = 'Pexels';
  const giphy = 'Giphy';
  const dropbox = 'Dropbox';
  const googleDrive = 'Google Drive';

  const portal = window.document.getElementById(`attachment-button-compose-${activeComposeBoxTab}`) as HTMLElement;

  const { x, y, strategy, refs } = useFloating({
    open: isMediaAttacherOpen,
    onOpenChange: composeMediaInteractions.setIsMediaAttacherOpen,
    placement: 'bottom-start',
    middleware: [offset({ mainAxis: 0, alignmentAxis: -4 }), shift(), flip({ fallbackPlacements: ['top-start', 'right-end', 'right-start'] })],
    whileElementsMounted: autoUpdate,
  });

  const onDeviceUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;

    setIsUploading(false);
    composeMediaInteractions.setIsMediaAttacherOpen(false);

    if (!files) return;
    validateAndUploadFiles(Array.from(files));
  };

  const handleClick = (option: string) => {
    composeMediaInteractions.setIsMediaAttacherOpen(false);

    if (option === recentlyUsed) return setIsRecentlyUsedModalOpen(true);
    if (option === fromUrl) return setIsURLModalOpen(true);
    if (option === canva) return setIsCanvaModalOpen(true);
    if (option === youtubeLink) return setIsYoutubeModalOpen(true);
    if (option === flickr) return setIsFlickrModalOpen(true);
    if (option === pexels) return setIsPexelsModalOpen(true);
    if (option === giphy) return setIsGiphyModalOpen(true);
  };

  const handleKeyDown = (key: KeyboardEvent) => {
    if (key.code === 'Escape') {
      composeMediaInteractions.setIsMediaAttacherOpen(false);
    }
  };

  const optionList: OptionList[] = [
    {
      icon: 'upload',
      id: 'mad-upload-from-device',
      label: t('upload_from_device'),
      extraLabel: t('or_drop_files') || '',
      onClick: () => fileInputRef.current?.click(),
      onKeyDown: (event: KeyboardEvent<HTMLElement>) => handleKeyDown(event),
      useStroke: true,
    },
    {
      icon: 'clock',
      id: 'mad-upload-recently-used',
      label: t('recently_used'),
      onClick: () => handleClick(recentlyUsed),
      onKeyDown: (event: KeyboardEvent<HTMLElement>) => handleKeyDown(event),
      useStroke: true,
    },
    {
      icon: 'globe',
      id: 'mad-upload-from-url',
      label: t('from_url'),
      onClick: () => handleClick(fromUrl),
      onKeyDown: (event: KeyboardEvent<HTMLElement>) => handleKeyDown(event),
      useStroke: true,
    },
    {
      icon: 'canva',
      id: 'mad-upload-canva',
      label: t('canva'),
      onClick: () => handleClick(canva),
      onKeyDown: (event: KeyboardEvent<HTMLElement>) => handleKeyDown(event),
    },
    {
      icon: 'youtube_color',
      id: 'mad-upload-youtube',
      label: t('youtube_link'),
      onClick: () => handleClick(youtubeLink),
      onKeyDown: (event: KeyboardEvent<HTMLElement>) => handleKeyDown(event),
    },
    {
      icon: 'flickr',
      id: 'mad-upload-flickr',
      label: t('flickr'),
      onClick: () => handleClick(flickr),
      onKeyDown: (event: KeyboardEvent<HTMLElement>) => handleKeyDown(event),
    },
    {
      icon: 'pexels',
      id: 'mad-upload-pexels',
      label: t('pexels'),
      onClick: () => handleClick(pexels),
      onKeyDown: (event: KeyboardEvent<HTMLElement>) => handleKeyDown(event),
    },
    {
      icon: 'giphy',
      id: 'mad-upload-giphy',
      label: t('giphy'),
      onClick: () => handleClick(giphy),
      onKeyDown: (event: KeyboardEvent<HTMLElement>) => handleKeyDown(event),
    },
    {
      icon: 'dropbox',
      id: 'mad-upload-dropbox',
      label: t('dropbox'),
      onClick: () => handleClick(dropbox),
      onKeyDown: (event: KeyboardEvent<HTMLElement>) => handleKeyDown(event),
    },
    {
      icon: 'google_drive',
      id: 'mad-upload-google-drive',
      label: t('google_drive'),
      onClick: () => handleClick(googleDrive),
      onKeyDown: (event: KeyboardEvent<HTMLElement>) => handleKeyDown(event),
    },
  ];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleBlur = (e: any) => {
    if (e?.relatedTarget?.id) {
      const listOfIdsToIgnoreBlur = optionList.map(({ id }) => id);

      if (listOfIdsToIgnoreBlur.includes(e.relatedTarget.id)) return;
    }

    if (!isUploading) composeMediaInteractions.setIsMediaAttacherOpen(false);
    else setIsUploading(false);
  };

  return (
    <>
      {createPortal(
        <Container
          ref={refs.setReference}
          onClick={() => {
            setIsUploading(false);
            composeMediaInteractions.toggleMediaAttacher();
          }}
          onBlur={handleBlur}
        />,
        portal
      )}
      {createPortal(
        <List
          ref={refs.setFloating}
          style={{ position: strategy, top: y ?? 0, left: x ?? 0 }}
          onBlur={handleBlur}
        >
          <HiddenInput
            accept="video/*,image/*, .pdf"
            multiple
            name="files[]"
            onChange={onDeviceUpload}
            onClick={(event) => {
              setIsUploading(true);
              event.currentTarget.value = '';
            }}
            ref={fileInputRef}
            type="file"
          />
          {optionList.map(({ icon, id, label, onKeyDown, onClick, extraLabel, useStroke }, index) => (
            <Fragment key={id}>
              <Option
                autoFocus={index === 0}
                extraLabel={extraLabel}
                icon={icon}
                id={id}
                label={label}
                onClick={onClick}
                onKeyDown={onKeyDown}
                useStroke={useStroke}
              />
              {index === 0 && <Spacer />}
            </Fragment>
          ))}
        </List>,
        document.getElementById('dropdown-root') as HTMLElement
      )}
    </>
  );
};
